import type { MoveIntent } from "../player/controller";

/*
 * Keyboard, mouse and the pointer lock.
 *
 * The client's only job here is to turn raw events into an *intention* — a
 * direction and a jump request — and hand it to the player controller. It never
 * decides what the intention means; in the fullness of Phase 1 the same
 * intentions become commands the sim reads (plan decision 6: no gameplay
 * decisions in JavaScript).
 *
 * Two details are not obvious and both are about lost input:
 *
 * - **Jump is latched, not sampled.** Key presses arrive between ticks; a jump
 *   sampled at tick time would be dropped whenever the player tapped the key
 *   inside a single frame. The latch is set by the event and cleared by the tick
 *   that consumes it. Digging and the replay export are latched the same way and
 *   for the same reason.
 * - **Losing the pointer lock clears the keys.** The browser stops delivering
 *   keyup once focus is gone, so a player who alt-tabs mid-stride would come
 *   back walking forever. The export latch survives it: that request is a host
 *   command rather than part of the walk, and losing it silently is worse than
 *   honouring it late.
 *
 * Since the touch pass there is a third input and it is not a third code path:
 * `input/touch.ts` turns two thumbs into a walk, a look and a verb, and pushes
 * all three through {@link Controls.setStick}, {@link Controls.pushLook} and
 * {@link Controls.press} into the *same* fields the keys and the mouse write. So
 * every latch, every consumption rule and every clearing above holds for a finger
 * too, and the frame loop drains exactly the doors it always drained. What touch
 * mode changes here is one thing only: there is no pointer lock to take, so
 * {@link Controls.requestLock} answers the gesture itself and
 * {@link Controls.locked} becomes a question about the *session* rather than
 * about the browser. The decision, its numbers and what was rejected are in
 * `docs/decisions/input.md`.
 */

/** Radians of view rotation per pixel of mouse movement. */
const LOOK_SENSITIVITY = 0.0022;

/** The movement keys, by `code` rather than `key`, so the layout does not matter. */
const FORWARD_KEYS = new Set(["KeyW"]);
const BACK_KEYS = new Set(["KeyS"]);
const LEFT_KEYS = new Set(["KeyA"]);
const RIGHT_KEYS = new Set(["KeyD"]);
const JUMP_KEYS = new Set(["Space"]);

/**
 * The keys that sprint while held. Both shifts, because which one a player
 * leans on is a habit, not a choice.
 */
const SPRINT_KEYS = new Set(["ShiftLeft", "ShiftRight"]);

/**
 * The keys that dig, alongside the mouse button.
 *
 * Same rationale as the look keys: the dig *click* has to stay behind the
 * pointer lock (an unlocked click is a UI gesture, and it is also the click
 * that acquires the lock), so a key is the path that keeps digging testable
 * when the lock misbehaves. It aims wherever the crosshair aims, however the
 * view got there.
 */
const DIG_KEYS = new Set(["KeyE"]);

/**
 * The key that interacts with whatever is in front of the player.
 *
 * F, next to the movement keys and pressed with the same hand that walked up to
 * somebody. It is a *proximity* verb rather than an aimed one — what it
 * addresses comes out of `witness/interact.ts`, not out of the crosshair ray —
 * so unlike digging it has no mouse-button twin: there is nothing to aim, and a
 * click already means "dig".
 */
const INTERACT_KEYS = new Set(["KeyF"]);

/**
 * The key that makes camp and waits for morning.
 *
 * C for camp, under the same hand as the movement keys — it is a thing you do
 * where you are standing, not a panel you open. Latched like the interact verb
 * and, like it, not re-latched while held: a night is something you decide to
 * sleep through once (Phase 5 decision 8, `main.ts`).
 *
 * The key is bound here whether or not there is a night to sleep through; what
 * decides whether pressing it does anything is the sky, which `main.ts` owns.
 * That is the same arrangement F and J are under — the binding costs a set
 * lookup and the verb answers for itself.
 */
const CAMP_KEYS = new Set(["KeyC"]);

/**
 * The key that opens the journal.
 *
 * J for journal, and it is a *toggle* rather than a latch-per-tick: the panel is
 * a piece of UI, and asking for it twice in one tick means asking for it back
 * again. See {@link Controls.takeJournalToggles}.
 */
const JOURNAL_KEYS = new Set(["KeyJ"]);

/**
 * The key that opens the map.
 *
 * M for map, beside J because it is the journal's sibling instrument — one is
 * what the player was told and the other is where they have been (Phase 5
 * decision 10, `map/ground.ts`). A toggle for the same reason the journal is
 * one: it is a piece of UI, and asking for it twice in one frame means asking
 * for it back again.
 */
const MAP_KEYS = new Set(["KeyM"]);

/**
 * The key that opens the trace view.
 *
 * T for trace, beside J because it is the journal read a second way — every
 * entry grouped under the record it is about, with the log's own sentence for
 * it (`hud/panels.ts`). A toggle for the same reason the journal is one.
 *
 * The key is bound here and the panel is only *wired* in development
 * (`main.ts`), which is the same shape the client uses for everything that
 * exists to check a claim rather than to play: the binding costs a set lookup on
 * keydown, and a production session has nothing listening.
 */
const TRACE_KEYS = new Set(["KeyT"]);

/**
 * The key that opens the menu (`hud/panels/menu.ts`, chunk U-3).
 *
 * Tab, which is where a system screen lives in every game that has one and
 * which this client had bound to nothing at all — the origins cards step on it,
 * but that is a listener on the birth screen and there is no session behind it
 * (`hud/panels/origins.ts`).
 *
 * The browser wants it too: Tab cycles focus, and while the player is in the
 * game there is nothing on the page a focus ring belongs on. So the handler
 * calls `preventDefault` on it — the same courtesy the arrows, the export key
 * and the debug keys get, and for the sharper version of their reason: the
 * screen this key opens has buttons in it, so a Tab that also stepped the
 * browser's focus would leave a tab strip walking under a player who pressed
 * the key once.
 *
 * A toggle rather than a latch, like every other panel here: the consumer
 * toggles, and asking for it twice in one frame means asking for it back again.
 */
const MENU_KEYS = new Set(["Tab"]);

/**
 * The ten keys the hotbar's ten slots sit under (`hud/panels/shared.ts`, chunk
 * U-5).
 *
 * **The row a keyboard has, in the order it has it**: `1`…`9` and then `0`,
 * which is the tenth slot and not the first — the strip is read left to right
 * and the last key on the row is the last box on the strip. The index into this
 * list *is* the slot, which is why it is an array and not a set: a set says
 * *this code is bound* and the handler needs *which of the ten*.
 *
 * All ten were unbound in this client until now, which the scouting pass
 * checked (`ui-graduation-plan.md` §8) and {@link BOUND_KEYS}' partition check
 * enforces from here on.
 *
 * Latched per press rather than counted like a panel toggle: a slot press is a
 * **verb** — one `Command::Wield` — and asking for it twice does not mean
 * asking for it back.
 */
const HOTBAR_CODES: readonly string[] = [
  "Digit1",
  "Digit2",
  "Digit3",
  "Digit4",
  "Digit5",
  "Digit6",
  "Digit7",
  "Digit8",
  "Digit9",
  "Digit0",
];

/** {@link HOTBAR_CODES} as the set every other binding here is, for `BOUND_KEYS`. */
const HOTBAR_KEYS = new Set(HOTBAR_CODES);

/**
 * The keys that open the debug window (`dev/debugpanel.ts`, wave 1.6 item 7).
 *
 * Backquote and F3, and both of them are the same guess made twice. The
 * backquote is where a developer's hand goes for a console and it is bound to
 * nothing else in this file; F3 is where a *player* of the game whose look this
 * project is aimed at goes for a debug screen, and the owner is both. Two codes
 * in one set is the shape {@link SPRINT_KEYS} already has, for its reason: which
 * one somebody reaches for is a habit, not a choice.
 *
 * F3 is the browser's legacy find-again key, so the handler calls
 * `preventDefault` on it — the same courtesy the arrows and the export key get.
 *
 * A toggle rather than a latch, like every other panel here. Bound whether or
 * not anything listens, and what listens is bound behind `?debug=1` or `?dev=1`
 * in `main.ts`: a playtester who fat-fingers a backquote must not meet the
 * log, which is the trace view's rule (Phase 4 decision 7) applied to a window
 * that — unlike the trace view — ships in production builds and says why in
 * `session/params.ts`.
 */
const DEBUG_KEYS = new Set(["Backquote", "F3"]);

/**
 * The key that takes the marketplace's work at whatever the crosshair is on.
 *
 * G for gather, and it is the *aimed* verb of the three the phase adds — it
 * names a tile through the crosshair ray, exactly as digging does, because what
 * it takes is work done **to** a tile (`world/types.ts`,
 * {@link WorldSource.gather}). Latched and consumed once per fixed step like the
 * dig, for the dig's reason: it becomes a command submitted to a tick.
 *
 * Bound whether or not there is a town promoted to have a board; what decides
 * whether pressing it does anything is the simulation, which answers by the
 * board changing or not changing.
 */
const GATHER_KEYS = new Set(["KeyG"]);

/**
 * The key that puts down the first thing the player is carrying.
 *
 * Q, beside the movement keys, and the pairing is deliberate: **picking up is
 * `F`** — a proximity verb on a heap the player walked up to, like everything
 * else in this world you address by standing in front of it — and putting down
 * is a key, because there is nothing out there to address. That asymmetry is the
 * two verbs being different questions rather than a scheme half-applied.
 *
 * Latched per step like the dig and the gather: a drop is a command.
 */
const DROP_KEYS = new Set(["KeyQ"]);

/**
 * The key that opens what the player is carrying.
 *
 * I for inventory, and a *toggle* rather than a latch for the journal's reason:
 * the panel is a piece of UI, and asking for it twice in one frame means asking
 * for it back again.
 */
const INVENTORY_KEYS = new Set(["KeyI"]);

/**
 * The key that looses an arrow at whoever the crosshair is on.
 *
 * R, and it is the second *aimed* verb after the dig — it names a **person**
 * through the same cone the strike pick uses, because a shot at a doorstep with
 * two people on it has to be able to say which (`world/types.ts`,
 * {@link WorldSource.loose}).
 *
 * A key of its own rather than a second meaning on the mouse, unlike the swing:
 * mouse-0 already carries two verbs and telling a player which of *three* a
 * button will do is a prompt nobody reads. What teaches this one is the same
 * prompt under the crosshair.
 *
 * Latched and not re-latched while held, like the strike it stands beside: a
 * shot is a decision rather than a thing you lean on, and the cooldown would
 * eat the extra presses anyway.
 */
const LOOSE_KEYS = new Set(["KeyR"]);

/**
 * The keys that put armour on and take it off.
 *
 * P for *put on* and O for *off* — adjacent, mnemonic, and free. They are the
 * hands panel's verbs rather than the world's: what they act on is what the
 * player is carrying, and the panel is where a player finds out that they have
 * something to put on at all (the owner's UI rule — menus own choices).
 *
 * **Which piece is not chosen here or anywhere client-side**: the simulation
 * picks the most protective thing that fits a bare part, so a press is a press
 * and never a selection (`sim_core::hands`).
 *
 * Latched without re-latching while held, on the drop's own reasoning: leaning
 * on a key should not dress or undress somebody one piece per tick.
 */
const WEAR_KEYS = new Set(["KeyP"]);
const DOFF_KEYS = new Set(["KeyO"]);

/**
 * The key that waits where the player is standing, until first light.
 *
 * X, and it is the one key in this file whose letter means nothing — it is the
 * one under the same hand as C that nothing else wanted. The **prompt** is what
 * teaches it ("X — wait until morning"), which is the standing rule for every
 * string this client shows: a word a player has to look up is a prompt that
 * failed (`witness/witness.ts`'s labels). It said *"a while"* until chunk Fx-2,
 * which was a promise the verb never made — a crossing always ends at a day
 * boundary.
 *
 * It is camp's sibling and shares camp's machinery — every tick genuinely runs
 * (`host/fastforward.ts`) — and differs from it in exactly one way: **a camp
 * waits out the night and this waits at any hour.** Latched like camp and, like
 * camp, not re-latched while held: waiting is a thing you decide once.
 */
const WAIT_KEYS = new Set(["KeyX"]);

/**
 * The key that lies up where the player is standing, until the simulation says
 * they are fit.
 *
 * **B, for the one thing it could sensibly be**, and it is the third of the
 * pass-time family after C and X (chunk Fx-5, the owner's ruling: *"offer an
 * option to skip time till recovered"*). The letter is free, it is under the
 * same hand as the other two, and it is the first letter of the word the prompt
 * uses — which is as much as a binding can ever explain about itself.
 *
 * It differs from its two siblings in **when it is worth pressing** and that is
 * a fact about the body rather than about the key: camp waits for dark, the wait
 * works at any hour, and this is offered while the simulation calls the player
 * unfit (`main.ts`'s `canRest`). It is also the only verb in this file that
 * means something while the screen is already black — a body that has gone under
 * can still say it would rather stay down until it is fit — so `main.ts` drains
 * it during a blackout as well.
 *
 * Latched and not re-latched while held, like camp and the wait: lying down is a
 * thing you decide once, and the auto-repeat would toggle the option thirty
 * times a second.
 */
const REST_KEYS = new Set(["KeyB"]);

/**
 * The key that lifts the player off the ground, in a development session only.
 *
 * **F4, and it is in the function row on purpose** (chunk Fx-3). F3 opens the
 * debug window and every letter this client has is either a verb or one press
 * away from being one, so a flight toggle on a letter would be a letter the next
 * player verb cannot have. The row above the keyboard is where this project has
 * already put the things that are *about* the session rather than in it — F3 for
 * the window, F9 for the replay — and a developer's flight is one of those.
 *
 * Bound in every session and **listened to in none but a `?dev=1` one**, which
 * is the arrangement the trace view has had since Phase 4 (`main.ts`): the
 * binding costs a set lookup on keydown, and a production session has nothing
 * wired to the latch. That is the whole of the gate, and it is stated again
 * where it is applied.
 *
 * F4 is a legacy address-bar shortcut in some browsers, so the handler calls
 * `preventDefault` on it — the same courtesy F3, F9 and the arrows get.
 *
 * A toggle rather than a latch, like every panel here: flight is a state you are
 * in, and asking for it twice in one frame means asking to land again.
 */
const FLY_KEYS = new Set(["F4"]);

/**
 * The keys that sink a flying player. Rising is {@link JUMP_KEYS}, held.
 *
 * **Space up and Z down, which is the pair every voxel game already taught
 * everybody** — and the up half is deliberately the jump key rather than a
 * fourth binding: while flight is on there is no jump to confuse it with, and a
 * developer who has just pressed F4 should not have to learn two keys to find
 * out whether the toggle worked. Z is the free key nearest the walk keys under
 * the same hand.
 *
 * Read as a *held* axis rather than latched ({@link Controls.flyLift}): height
 * is a thing you hold, exactly as walking is, and a latch would make a flight
 * rise in thirty-a-second hops.
 *
 * The descent key is a new binding and the ascent key is one that already
 * existed, so only this one appears in {@link BOUND_KEYS} under a name of its
 * own — what a set there means is *which verb owns this code*, and Space is
 * still the jump's.
 */
const SINK_KEYS = new Set(["KeyZ"]);

/**
 * The look keys: arrows turn the view instead of aliasing WASD.
 *
 * They exist so the client can be driven with no working mouse look at all —
 * keyboard input deliberately does not require the pointer lock (see
 * `handleKeyDown`), so arrows answer "is everything else fine?" the moment the
 * lock misbehaves, which is exactly when a mouse-only scheme goes dark.
 */
const LOOK_LEFT_KEYS = new Set(["ArrowLeft"]);
const LOOK_RIGHT_KEYS = new Set(["ArrowRight"]);
const LOOK_UP_KEYS = new Set(["ArrowUp"]);
const LOOK_DOWN_KEYS = new Set(["ArrowDown"]);

/** Radians per second of view rotation while a look key is held. */
const KEY_LOOK_YAW_RATE = 2.4;
const KEY_LOOK_PITCH_RATE = 1.6;

/**
 * The keys that ask for the session's replay file.
 *
 * F9 because it collides with nothing a browser or an OS wants: F5 reloads, F11
 * is fullscreen, F12 is devtools, and every letter is a movement key waiting to
 * happen. Works whether or not the pointer is locked — it is a request about the
 * session, not a move in it.
 */
const EXPORT_KEYS = new Set(["F9"]);

/** The mouse button that digs. Left, and only left. */
const DIG_BUTTON = 0;

/**
 * Every verb an on-screen button can fire (`input/touch.ts`).
 *
 * The touch pass's whole contract with this file: a finger presses a button, the
 * button names a verb, and the verb sets **the same latch the key sets**. There
 * is no second path into the game — {@link Controls.press} is a switch over this
 * union and nothing else, so a verb the keyboard cannot fire is a verb a thumb
 * cannot fire either.
 */
export type TouchVerb =
  | "jump"
  | "dig"
  | "strike"
  | "loose"
  | "interact"
  | "gather"
  | "drop"
  | "wear"
  | "doff"
  | "camp"
  | "wait"
  | "rest"
  | "inventory"
  | "journal"
  | "map"
  | "menu"
  | "trace"
  | "debug"
  | "export";

/** A door a verb comes back out of: a `take*` member of {@link Controls}, and nothing else. */
type TouchDoor = Extract<keyof Controls, `take${string}`>;

/**
 * Each verb, and the door the frame loop reads it back through.
 *
 * Exported for {@link BOUND_KEYS}'s reason, which is a testing one: the
 * self-checks cannot press an on-screen button without pressing it at the
 * session's own `Controls`, so what they read instead is this table — **every
 * button names a verb, and every verb has a door `main.ts` already drains.** A
 * button wired to a latch nobody consumes is a button that does nothing, and it
 * is invisible from anywhere else.
 *
 * The door names are `keyof Controls` filtered to the `take*` family, so the day
 * one of them is renamed is the day this stops compiling — the same one-line
 * compiler check `main.ts` gets out of assigning the world to a `BirthDoor`.
 */
export const TOUCH_VERBS: readonly (readonly [TouchVerb, TouchDoor])[] = [
  // Jump comes back inside the movement intent rather than through a door of its
  // own, which is the one asymmetry in this table and it is the walk's: a jump is
  // part of what the player is doing this tick, not a request about the session.
  ["jump", "takeIntent"],
  ["dig", "takeDig"],
  // **A door of its own rather than the dig's** (wave 2D-a). On a desktop the
  // left button means *dig* or *strike* by what the crosshair is on, and
  // `main.ts` routes the one latch by asking the witnessing layer which
  // (`Witnessing.wouldSwing`); a thumb has no crosshair to disambiguate with, so
  // it gets the verb as a button and this is the latch that button sets.
  ["strike", "takeStrike"],
  // The bow's own button, beside the swing's and for its reason: a thumb has no
  // crosshair to disambiguate with, so every verb a key can fire is a button.
  ["loose", "takeLoose"],
  ["interact", "takeInteract"],
  ["gather", "takeGather"],
  ["drop", "takeDrop"],
  ["wear", "takeWear"],
  ["doff", "takeDoff"],
  ["camp", "takeCamp"],
  ["wait", "takeWait"],
  // The third of the pass-time family (chunk Fx-5). A thumb needs it for a
  // sharper version of the reason it needs the other two: it is the one verb
  // that means anything while the screen is black, so a session played with
  // fingers would otherwise have no way to answer the offer in the dark.
  ["rest", "takeRest"],
  ["inventory", "takeInventoryToggles"],
  ["journal", "takeJournalToggles"],
  ["map", "takeMapToggles"],
  // The screen those three are pages of (chunk U-3). It is the **only** one of
  // the four a thumb has a button for: the ⋯ row had three buttons that each
  // opened one overlay and has one that opens the screen, and the tabs inside
  // it are what a finger presses to change page. The three verbs above stay in
  // this table because their KEYS stay bound — `J`, `M` and `I` land on a page
  // — and a verb in here with no button is exactly what `input/touch.ts`'s own
  // check allows: the rule is that every button names a verb, not that every
  // verb is drawn.
  ["menu", "takeMenuToggles"],
  ["trace", "takeTraceToggles"],
  ["debug", "takeDebugToggles"],
  ["export", "takeExport"],
];

/**
 * `Element.requestPointerLock` takes an options bag and returns a promise in
 * current browsers; the DOM typings lag the specification. Declared locally
 * rather than cast at the call site so the shape being assumed is written down.
 */
type PointerLockRequest = (options?: { unadjustedMovement?: boolean }) => Promise<void> | undefined;

export class Controls {
  private readonly target: HTMLElement;
  private readonly held = new Set<string>();

  private jumpLatched = false;
  private digLatched = false;
  private strikeLatched = false;
  private looseLatched = false;
  private wearLatched = false;
  private doffLatched = false;
  private exportLatched = false;
  private interactLatched = false;
  private campLatched = false;
  private gatherLatched = false;
  private dropLatched = false;
  private waitLatched = false;
  private restLatched = false;

  /**
   * How many times the journal key has been pressed since the last read.
   *
   * A count rather than a boolean, because the consumer *toggles*: two presses
   * inside one frame have to cancel out, and a boolean would turn them into one.
   */
  private journalToggles = 0;

  /** The same count for the map. See {@link Controls.takeMapToggles}. */
  private mapToggles = 0;

  /** The same count for the trace view. See {@link Controls.takeTraceToggles}. */
  private traceToggles = 0;

  /** The same count for the inventory. See {@link Controls.takeInventoryToggles}. */
  private inventoryToggles = 0;

  /** The same count for the menu. See {@link Controls.takeMenuToggles}. */
  private menuToggles = 0;

  /** The same count for the debug window. See {@link Controls.takeDebugToggles}. */
  private debugToggles = 0;

  /**
   * Which hotbar slots have been pressed since the last read, in the order they
   * were pressed (chunk U-5).
   *
   * A list rather than a count or a latch, because the *identity* of the press
   * is the whole of it: two digits inside one frame are two different slots and
   * a boolean would lose which, where the panel keys are one question asked
   * twice and cancel out. Drained by `main.ts` into
   * {@link Hotbar.press} on the frame they arrive.
   */
  private hotbarPresses: number[] = [];

  /**
   * The same count for the dev flight toggle. See {@link Controls.takeFlyToggles}.
   *
   * Counted here in every session and **drained only by a `?dev=1` one**
   * (`main.ts`): a latch nobody consumes is a key press waiting for the next
   * consumer, and there is never one in a production session because nothing
   * there constructs the tool.
   */
  private flyToggles = 0;
  private pendingYaw = 0;
  private pendingPitch = 0;
  private lockedNow = false;

  /**
   * Whether this session is played with fingers instead of a mouse
   * (`input/touch.ts`).
   *
   * It changes exactly two things here and neither is a second code path: the
   * pointer lock is not asked for (there is nothing to capture on a touchscreen
   * and the request would fail), and {@link Controls.locked} answers about the
   * *session* rather than about the browser's lock. Everything below it —
   * `lockedNow`, the mouse handlers, the key handlers — is untouched, so a
   * desktop session takes the same branch it always took.
   */
  private readonly touchMode: boolean;

  /**
   * Whether the touch session has been taken: touch mode's answer to the lock.
   *
   * A desktop player clicks and the browser grants a pointer lock; a touch player
   * taps and there is nothing to grant, so this is what "the player is in the
   * game now" means there. It is what takes the tap-to-play overlay down, through
   * the same `onLockChange` the lock fires (`main.ts`), and it never goes back to
   * `false` — there is no Escape on a phone and no reason to invent one.
   */
  private touchTaken = false;

  /**
   * The move stick's last reading, held until a finger moves it or lets go.
   *
   * Analogue where the keys are a sign: the deflection *is* the speed, because
   * `PlayerController` only normalises a move vector longer than one
   * (`player/controller.ts`). Held rather than latched for the reason sprint is
   * sampled rather than latched — walking is a state a thumb is in.
   */
  private stickForward = 0;
  private stickStrafe = 0;
  private stickSprint = false;

  /**
   * Whether the player has done anything in the world yet.
   *
   * What the first-run key card is dismissed on (Phase 4 decision 7,
   * `hud/panels.ts`), and it is deliberately **not** the click that starts the
   * session: that click is the gesture that acquires the pointer lock, so a card
   * dismissed by it would be a card that vanished at the exact moment the player
   * first looked at the game. A key, or the mouse moving inside the lock, is
   * somebody playing.
   */
  private played = false;

  /** Called whenever the pointer lock is gained or lost, so the HUD can say so. */
  private readonly onLockChange: (locked: boolean) => void;

  /**
   * Called when a pointer-lock request fails, with a human-readable reason.
   *
   * The failure used to be swallowed, which reads as "mouse look is broken"
   * with no clue attached — on platforms without `unadjustedMovement` support
   * even the *first* request rejects, and the retry can lose the user gesture
   * that authorised it. The HUD gets to say so instead.
   */
  private readonly onLockError: (message: string) => void;

  constructor(
    target: HTMLElement,
    onLockChange: (locked: boolean) => void,
    onLockError: (message: string) => void,
    touch = false,
  ) {
    this.target = target;
    this.onLockChange = onLockChange;
    this.onLockError = onLockError;
    this.touchMode = touch;

    target.addEventListener("click", this.handleClick);
    document.addEventListener("pointerlockchange", this.handleLockChange);
    document.addEventListener("pointerlockerror", this.handleLockError);
    document.addEventListener("keydown", this.handleKeyDown);
    document.addEventListener("keyup", this.handleKeyUp);
    document.addEventListener("mousedown", this.handleMouseDown);
    document.addEventListener("mousemove", this.handleMouseMove);
    window.addEventListener("blur", this.handleBlur);
  }

  /**
   * Whether the player is *in* the session: the browser's pointer lock, or the
   * tap that stands in for it.
   *
   * The question every caller was already asking — the overlay's "why is the
   * mouse free", the reading panels' handshake — so the touch answer joins it
   * here rather than at three call sites (`main.ts`). Identical on a desktop:
   * {@link Controls.touchTaken} is `false` for the life of a mouse session.
   *
   * The handlers below deliberately keep asking `lockedNow` instead: mouse look
   * and the dig click need *movement deltas and a real capture*, which a tap does
   * not provide, and a compatibility mouse event synthesised behind a finger must
   * not dig.
   */
  get locked(): boolean {
    return this.lockedNow || this.touchTaken;
  }

  /**
   * Whether the player has moved, looked or pressed anything yet.
   *
   * Read every frame and never consumed, unlike the latches: it is a fact about
   * the session rather than an event, and the one thing that reads it takes
   * itself off the screen for good the first time it is true.
   */
  get hasPlayed(): boolean {
    return this.played;
  }

  /**
   * The movement intention for one tick, consuming the jump latch.
   *
   * Called exactly once per fixed step. Calling it twice in a tick would eat a
   * jump; calling it zero times would hold one until the next tick, which is the
   * behaviour we want when the clamp drops a frame's worth of steps.
   */
  takeIntent(): MoveIntent {
    const jump = this.jumpLatched;
    this.jumpLatched = false;

    // The keys answer where a key is held and the stick answers where none is.
    // It is a merge that never fires in either session it was written for — a
    // phone has no keys and a desktop's stick is zero for the life of the tab —
    // and it is spelled anyway, because a hybrid device has both and the rule
    // "the thing the player is touching wins" has to be written somewhere.
    const keyForward = axis(this.held, FORWARD_KEYS, BACK_KEYS);
    const keyStrafe = axis(this.held, RIGHT_KEYS, LEFT_KEYS);

    return {
      forward: keyForward === 0 ? this.stickForward : keyForward,
      strafe: keyStrafe === 0 ? this.stickStrafe : keyStrafe,
      jump,
      // Sampled rather than latched, unlike the jump: sprinting is a state the
      // key is held in, and a latch would turn a tap into a stride. The stick's
      // own sprint is the same state — the thumb past the ring's edge.
      sprint: anyHeld(this.held, SPRINT_KEYS) || this.stickSprint,
    };
  }

  /**
   * Whether the player asked to dig since the last call, consuming the latch.
   *
   * Called once per fixed step, like {@link Controls.takeIntent}: a dig becomes
   * a command submitted to a tick, so one click is one dig however many frames
   * the click spanned.
   */
  takeDig(): boolean {
    const dig = this.digLatched;
    this.digLatched = false;

    return dig;
  }

  /**
   * Whether the player asked to swing since the last call, consuming the latch.
   *
   * Drained once per fixed step beside {@link Controls.takeDig} and for its
   * reason: a swing becomes a command submitted to a tick, so one press is one
   * swing however many frames it spanned.
   *
   * **Set only by the on-screen button, never by a key or the mouse.** A desktop
   * session swings through the *dig* latch, routed by what the crosshair is on
   * (`main.ts`, and {@link TOUCH_VERBS}'s note), because one button that means
   * the obvious thing is better than two the player has to choose between while
   * somebody is hitting them. Held down it does not re-latch, unlike the dig: a
   * fight is a series of decisions rather than a thing you lean on.
   */
  takeStrike(): boolean {
    const wanted = this.strikeLatched;
    this.strikeLatched = false;

    return wanted;
  }

  /**
   * Whether the player asked to loose an arrow since the last call, consuming
   * the latch.
   *
   * Drained once per fixed step beside {@link Controls.takeStrike} and under its
   * rule: a shot becomes a command submitted to a tick, and a held key does not
   * re-latch because a fight is a series of decisions.
   */
  takeLoose(): boolean {
    const wanted = this.looseLatched;
    this.looseLatched = false;

    return wanted;
  }

  /**
   * Whether the player asked to put armour on since the last call, consuming the
   * latch.
   *
   * Drained per step like the drop, and not re-latched while held for its
   * reason: a lean on a key should not dress somebody one piece per tick.
   */
  takeWear(): boolean {
    const wanted = this.wearLatched;
    this.wearLatched = false;

    return wanted;
  }

  /** Whether the player asked to take armour off. {@link Controls.takeWear}'s mirror. */
  takeDoff(): boolean {
    const wanted = this.doffLatched;
    this.doffLatched = false;

    return wanted;
  }

  /**
   * Whether the player asked to gather since the last call, consuming the latch.
   *
   * Drained once per fixed step beside {@link Controls.takeDig} and for its
   * reason: a gather becomes a command submitted to a tick, so one press is one
   * claim however many frames the press spanned. Held down it re-latches like
   * the dig — the board refuses work somebody already has, so leaning on it is
   * one claim and then silence.
   */
  takeGather(): boolean {
    const wanted = this.gatherLatched;
    this.gatherLatched = false;

    return wanted;
  }

  /**
   * Whether the player asked to put something down since the last call,
   * consuming the latch.
   *
   * Drained per step like the gather. Not re-latched while held, unlike the
   * dig: emptying a pair of hands one item per tick by leaning on a key is not
   * a thing anybody means to do.
   */
  takeDrop(): boolean {
    const wanted = this.dropLatched;
    this.dropLatched = false;

    return wanted;
  }

  /**
   * Whether the player asked to wait since the last call, consuming the latch.
   *
   * Drained once per frame beside {@link Controls.takeCamp}, which is the verb
   * it shares its machinery with: it queues no command of its own and what it
   * does is run the world forward (`main.ts`).
   */
  takeWait(): boolean {
    const wanted = this.waitLatched;
    this.waitLatched = false;

    return wanted;
  }

  /**
   * How many times the inventory was asked for since the last call, consuming
   * the count.
   *
   * Counted and consumed exactly like the journal's, and for its reason: the
   * consumer toggles, so two presses inside one frame have to cancel out.
   */
  takeInventoryToggles(): number {
    const toggles = this.inventoryToggles;
    this.inventoryToggles = 0;

    return toggles;
  }

  /** Whether the player asked for the replay file since the last call, consuming the latch. */
  takeExport(): boolean {
    const wanted = this.exportLatched;
    this.exportLatched = false;

    return wanted;
  }

  /**
   * Whether the player asked to interact since the last call, consuming the
   * latch.
   *
   * Latched like the dig for the same reason — a key press arrives between
   * frames and a sampled one would be dropped. Consumed once per *frame* rather
   * than once per fixed step, unlike the dig: an interaction queues no command
   * and takes effect in no tick (plan decision 10), so tying it to the step
   * would only make it later. Held down it does not re-latch, unlike jump: a
   * conversation is a thing you start, not a thing you lean on.
   */
  takeInteract(): boolean {
    const wanted = this.interactLatched;
    this.interactLatched = false;

    return wanted;
  }

  /**
   * Whether the player asked to lie up since the last call, consuming the latch.
   *
   * Drained once per frame beside {@link Controls.takeCamp} and
   * {@link Controls.takeWait}, which are the two verbs it shares its machinery
   * with. Whether there is anything to rest through — and whether the press is a
   * verb or the option under a blackout — is not this layer's question
   * (`main.ts`, `host/rest.ts`).
   */
  takeRest(): boolean {
    const wanted = this.restLatched;
    this.restLatched = false;

    return wanted;
  }

  /**
   * Whether the player asked to make camp since the last call, consuming the
   * latch.
   *
   * Drained once per frame beside the interact verb and for the same reasons:
   * it queues no command, and a key press that arrived between frames would be
   * dropped if it were sampled instead of latched. Whether there is any night
   * to sleep through is not this layer's question (`main.ts`).
   */
  takeCamp(): boolean {
    const wanted = this.campLatched;
    this.campLatched = false;

    return wanted;
  }

  /**
   * How many times the journal was asked for since the last call, consuming the
   * count.
   *
   * The caller applies each one as a toggle. Returning the count rather than a
   * flag is what makes "pressed twice in a frame" mean "still closed".
   */
  takeJournalToggles(): number {
    const toggles = this.journalToggles;
    this.journalToggles = 0;

    return toggles;
  }

  /**
   * How many times the map was asked for since the last call, consuming the
   * count.
   *
   * Counted and consumed exactly like the journal's. The key is bound whether
   * or not there is any ground surveyed to look at; what the map has on it is
   * not this layer's question (`map/ground.ts`).
   */
  takeMapToggles(): number {
    const toggles = this.mapToggles;
    this.mapToggles = 0;

    return toggles;
  }

  /**
   * How many times the trace view was asked for since the last call, consuming
   * the count.
   *
   * Counted and consumed exactly like the journal's, and consumed whether or not
   * anything is listening: a latch nobody drains is a key press that fires the
   * next time somebody does.
   */
  takeTraceToggles(): number {
    const toggles = this.traceToggles;
    this.traceToggles = 0;

    return toggles;
  }

  /**
   * How many times the menu was asked for since the last call, consuming the
   * count.
   *
   * Counted and consumed exactly like the journal's, and it is the door **both**
   * the `Tab` key and the pad's *menu* button come back out of — one screen,
   * one latch, one drain in `main.ts`. Which page it opens on is not this
   * layer's question: the screen remembers where the player left it.
   */
  takeMenuToggles(): number {
    const toggles = this.menuToggles;
    this.menuToggles = 0;

    return toggles;
  }

  /**
   * Which hotbar slots were pressed since the last call, consuming them (chunk
   * U-5).
   *
   * Zero-based, so the answer is an index into the strip rather than the digit
   * on the key — `0` is the slot marked `1` and `9` is the one marked `0`. What
   * is in a slot is not this layer's question: this says a key was pressed and
   * `hud/panels/shared.ts`'s `Hotbar` says whether there is anything under it.
   *
   * Drained whether or not anything is listening, like every other door here: a
   * latch nobody drains is a key press that fires the next time somebody does.
   */
  takeHotbarPresses(): readonly number[] {
    const pressed = this.hotbarPresses;
    this.hotbarPresses = [];

    return pressed;
  }

  /**
   * How many times the debug window was asked for since the last call,
   * consuming the count.
   *
   * Counted and consumed exactly like the trace view's, and drained whether or
   * not anything is listening for the same reason: a latch nobody drains is a
   * key press that fires the next time somebody does.
   */
  takeDebugToggles(): number {
    const toggles = this.debugToggles;
    this.debugToggles = 0;

    return toggles;
  }

  /**
   * How many times flight was asked for since the last call, consuming the
   * count (chunk Fx-3).
   *
   * Counted and consumed exactly like the debug window's. **Nothing drains it
   * outside a `?dev=1` session**, which is where the gate is: the key is bound
   * here whatever the session, and only a development one builds the tool that
   * reads this (`main.ts`, `dev/flight.ts`).
   */
  takeFlyToggles(): number {
    const toggles = this.flyToggles;
    this.flyToggles = 0;

    return toggles;
  }

  /**
   * Which way a flying player is being pushed vertically: 1 up, -1 down, 0
   * neither (chunk Fx-3).
   *
   * **Sampled rather than latched, and held rather than consumed**, which is the
   * sprint's rule rather than the jump's: height while flying is a state a key
   * is held in, and a latch would turn a lean on the key into a hop. Read only
   * by the dev flight tool; the walk's own {@link MoveIntent} is untouched by
   * this chunk, because a field on it that means nothing while nobody is flying
   * would be a development idea inside the production walk.
   */
  get flyLift(): number {
    return axis(this.held, JUMP_KEYS, SINK_KEYS);
  }

  /**
   * The view rotation accumulated since the last call, in radians, consuming it.
   *
   * Called once per rendered frame rather than per tick — see the note on look
   * latency in `player/controller.ts`. `frameSeconds` scales the held look
   * keys, which are a rate; the mouse contributes deltas and needs no clock.
   */
  takeLook(frameSeconds: number): { yaw: number; pitch: number } {
    // Positive yaw turns left and positive pitch looks up, matching the mouse
    // handler's convention below.
    const keyYaw = axis(this.held, LOOK_LEFT_KEYS, LOOK_RIGHT_KEYS) * KEY_LOOK_YAW_RATE;
    const keyPitch = axis(this.held, LOOK_UP_KEYS, LOOK_DOWN_KEYS) * KEY_LOOK_PITCH_RATE;

    const look = {
      yaw: this.pendingYaw + keyYaw * frameSeconds,
      pitch: this.pendingPitch + keyPitch * frameSeconds,
    };
    this.pendingYaw = 0;
    this.pendingPitch = 0;

    return look;
  }

  /*
   * ---------------------- What a thumb pushes in ----------------------
   *
   * The three entry points the touch layer uses (`input/touch.ts`), and the
   * whole of what it is allowed to do: set the walk, add to the look, fire a
   * verb. Every one of them writes the field a *key* writes, so nothing below
   * this line has to know a finger was involved and the frame loop drains
   * exactly the doors it always drained (`main.ts`).
   *
   * Methods rather than synthesised events, deliberately: the listeners in this
   * file are on `document`, so a layer that dispatched a fake `keydown` would be
   * pressing a key at *every* `Controls` on the page — the objection
   * {@link BOUND_KEYS} exists to work around — and a fake key press is also a
   * thing an extension or a future panel could see.
   */

  /**
   * The move stick's reading: a walk direction in -1..1 and whether it is a run.
   *
   * Held rather than consumed, unlike everything above: this is the state a
   * thumb is in, and it stays true until the finger moves or lets go. Zero is
   * how the touch layer says the finger has gone, which is why there is no
   * separate release.
   */
  setStick(forward: number, strafe: number, sprint: boolean): void {
    this.stickForward = forward;
    this.stickStrafe = strafe;
    this.stickSprint = sprint;

    if (forward !== 0 || strafe !== 0) {
      // Walking is playing, on the same rule a key press is: somebody who has
      // moved has stopped reading the card.
      this.played = true;
    }
  }

  /**
   * The look stick's drag, in radians, accumulated for the next frame.
   *
   * Straight into the pending accumulators the mouse writes, which means it
   * inherits everything already decided about them for free: the sign
   * convention, the drain in {@link Controls.takeLook}, and the clearing on
   * blur. Radians rather than pixels because the *rate* is a fact about the
   * screen a finger is dragging on (`input/touch.ts`), and this file has no
   * business knowing how wide that is.
   */
  pushLook(yaw: number, pitch: number): void {
    this.played = true;
    this.pendingYaw += yaw;
    this.pendingPitch += pitch;
  }

  /**
   * Fires one on-screen button's verb, exactly as its key does ({@link TOUCH_VERBS}).
   *
   * One press is one verb: nothing here re-latches while a finger rests on a
   * button, unlike a held `Space` or a held `E`. A key auto-repeats because the
   * OS says so and a button does not, and a thumb parked on *jump* while the
   * other hand looks around is a thumb parked, not a player hopping.
   */
  press(verb: TouchVerb): void {
    // Every button press is somebody playing, including the ones that open a
    // panel — the same rule `handleKeyDown` applies to any key at all.
    this.played = true;

    switch (verb) {
      case "jump":
        this.jumpLatched = true;
        break;
      case "dig":
        this.digLatched = true;
        break;
      case "strike":
        this.strikeLatched = true;
        break;
      case "loose":
        this.looseLatched = true;
        break;
      case "wear":
        this.wearLatched = true;
        break;
      case "doff":
        this.doffLatched = true;
        break;
      case "interact":
        this.interactLatched = true;
        break;
      case "gather":
        this.gatherLatched = true;
        break;
      case "drop":
        this.dropLatched = true;
        break;
      case "camp":
        this.campLatched = true;
        break;
      case "wait":
        this.waitLatched = true;
        break;
      case "rest":
        this.restLatched = true;
        break;
      case "export":
        this.exportLatched = true;
        break;
      case "inventory":
        this.inventoryToggles += 1;
        break;
      case "journal":
        this.journalToggles += 1;
        break;
      case "map":
        this.mapToggles += 1;
        break;
      case "menu":
        this.menuToggles += 1;
        break;
      case "trace":
        this.traceToggles += 1;
        break;
      case "debug":
        this.debugToggles += 1;
        break;
    }
  }

  /**
   * Asks the browser for the pointer lock, the way a click on the target does.
   *
   * Public because the click is not the only gesture that means "capture the
   * mouse": closing the journal means it too, and making the player click for
   * what they already asked for with J would be the client forgetting what J
   * meant. The J press is a user gesture, so the browser honours the request in
   * the ordinary case; the known refusal is the cooldown after an Escape exit,
   * and that lands on `onLockError` and the click-to-play overlay like any
   * other. Does nothing while the lock is already held.
   */
  requestLock(): void {
    if (this.touchMode) {
      // Nothing to capture: a touchscreen has no cursor to hide and no movement
      // deltas to unadjust, and every browser on one refuses the request. What
      // the gesture means is the same thing it means on a desktop — *I am
      // playing now* — so it is answered here rather than by the browser, and
      // announced through the same callback so the overlay comes down by the
      // path it already comes down by.
      if (!this.touchTaken) {
        this.touchTaken = true;
        this.played = true;
        this.onLockChange(true);
      }

      return;
    }

    if (this.lockedNow) {
      return;
    }

    const request = this.target.requestPointerLock as unknown as PointerLockRequest;
    // Unadjusted movement asks the OS to skip pointer acceleration, which is the
    // difference between mouse look that feels like a game and mouse look that
    // feels like a desktop. Not every platform supports it, and the rejection is
    // asynchronous, so the fallback is a second request without the option.
    const result = request.call(this.target, { unadjustedMovement: true });

    if (result instanceof Promise) {
      result.catch(() => {
        const plain = this.target.requestPointerLock as unknown as PointerLockRequest;
        const retry = plain.call(this.target);

        if (retry instanceof Promise) {
          retry.catch((error: unknown) => {
            // Nothing more to try; say why instead of going quiet.
            this.onLockError(lockErrorMessage(error));
          });
        }
      });
    }
  }

  /**
   * Gives the pointer lock back to the browser, if the target holds it.
   *
   * The other half of the exchange above: a reading overlay wants the mouse as
   * a cursor and a wheel, not as a camera. The lock-change event does the
   * bookkeeping, exactly as when the player leaves with Escape.
   */
  releaseLock(): void {
    if (this.touchMode) {
      // Nothing was borrowed, so nothing is returned. A touch player reading the
      // journal still has both thumbs — the panel takes the taps that land on it
      // and the sticks take the rest — so there is no capture to hand back and,
      // more to the point, no way to ask for one afterwards: a second tap would
      // be needed and the player never asked to be put back.
      return;
    }

    if (this.lockedNow) {
      document.exitPointerLock();
    }
  }

  /**
   * Drops every latch and every held key, as a blur does.
   *
   * **What a menu opened over a running session needs** (wave 2D-a's death
   * screen, `main.ts`): the listeners here are on `document` and stay bound
   * while the origins panel is up, so a key pressed at a menu would otherwise be
   * a jump, a swing or a camp waiting for the next life to start. Called every
   * frame the world is handed over rather than once, because a key held down
   * keeps re-latching.
   *
   * It is {@link Controls.releaseAll} under a name a caller can say, and it is
   * the same clearing the browser's own `blur` does — which is the right
   * precedent: a session nobody is playing has nothing held down in it.
   */
  forget(): void {
    this.releaseAll();
  }

  /** Detaches every listener. */
  dispose(): void {
    this.target.removeEventListener("click", this.handleClick);
    document.removeEventListener("pointerlockchange", this.handleLockChange);
    document.removeEventListener("pointerlockerror", this.handleLockError);
    document.removeEventListener("keydown", this.handleKeyDown);
    document.removeEventListener("keyup", this.handleKeyUp);
    document.removeEventListener("mousedown", this.handleMouseDown);
    document.removeEventListener("mousemove", this.handleMouseMove);
    window.removeEventListener("blur", this.handleBlur);
  }

  private readonly handleClick = (): void => {
    this.requestLock();
  };

  private readonly handleLockChange = (): void => {
    this.lockedNow = document.pointerLockElement === this.target;

    if (!this.lockedNow) {
      this.releaseAll();
    }

    this.onLockChange(this.lockedNow);
  };

  private readonly handleLockError = (): void => {
    this.lockedNow = false;
    this.releaseAll();
    this.onLockChange(false);
    this.onLockError("the browser refused the pointer lock");
  };

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    // Any key at all, including the ones this file ignores: somebody pressing
    // something is somebody who has stopped reading the card.
    this.played = true;

    if (EXPORT_KEYS.has(event.code)) {
      this.exportLatched = true;
      event.preventDefault();

      return;
    }

    // Keyboard input deliberately works without the pointer lock: walking and
    // arrow-look are how the client stays testable when the lock misbehaves,
    // and nothing a key does needs the mouse captured. Only mouse look and the
    // dig click stay behind the lock, because movement deltas and a safe
    // "this click is play, not UI" signal only exist inside it.
    if (JUMP_KEYS.has(event.code)) {
      // Held space keeps the latch set, so holding jump hops repeatedly rather
      // than jumping once and going quiet.
      this.jumpLatched = true;
      // The page does not scroll while playing.
      event.preventDefault();
    }

    if (DIG_KEYS.has(event.code)) {
      // Latched like the click and consumed once per tick, so a held key digs
      // once per press-and-tick rather than machine-gunning per frame.
      this.digLatched = true;
    }

    if (INTERACT_KEYS.has(event.code) && !event.repeat) {
      // `repeat` is the difference between this and the dig: the OS auto-repeat
      // would otherwise re-open the market panel thirty times a second while
      // the key is held, and a panel that flickers is a panel that is unusable.
      this.interactLatched = true;
    }

    if (GATHER_KEYS.has(event.code)) {
      // Re-latched while held, like the dig above and for its reason: a held
      // key claims once per press-and-tick rather than machine-gunning.
      this.gatherLatched = true;
    }

    if (DROP_KEYS.has(event.code) && !event.repeat) {
      // `repeat` unlike the gather: the OS auto-repeat would empty a pair of
      // hands in a quarter of a second, and a verb that does that on a lean is
      // a verb nobody can use.
      this.dropLatched = true;
    }

    if (LOOSE_KEYS.has(event.code) && !event.repeat) {
      // `repeat` like the drop rather than like the dig: a held key would ask
      // for a shot every frame and the cooldown would refuse all but one of
      // them in silence, which is the graybox ruling's own anti-pattern.
      this.looseLatched = true;
    }

    if (WEAR_KEYS.has(event.code) && !event.repeat) {
      this.wearLatched = true;
    }

    if (DOFF_KEYS.has(event.code) && !event.repeat) {
      this.doffLatched = true;
    }

    if (WAIT_KEYS.has(event.code) && !event.repeat) {
      // `repeat` for the reason camp checks it: a held key would ask to wait
      // thirty times a second and the second ask would land inside the first.
      this.waitLatched = true;
    }

    if (REST_KEYS.has(event.code) && !event.repeat) {
      // `repeat` for the wait's reason and one of its own: under a blackout this
      // key is a toggle, and a held key would flip the option thirty times a
      // second and settle wherever the finger came off.
      this.restLatched = true;
    }

    if (INVENTORY_KEYS.has(event.code) && !event.repeat) {
      this.inventoryToggles += 1;
    }

    if (CAMP_KEYS.has(event.code) && !event.repeat) {
      // `repeat` for the reason the interact verb checks it: a held key would
      // otherwise ask to sleep thirty times a second, and the second ask would
      // arrive in the middle of the first night.
      this.campLatched = true;
    }

    if (JOURNAL_KEYS.has(event.code) && !event.repeat) {
      this.journalToggles += 1;
    }

    if (MAP_KEYS.has(event.code) && !event.repeat) {
      this.mapToggles += 1;
    }

    if (TRACE_KEYS.has(event.code) && !event.repeat) {
      this.traceToggles += 1;
    }

    if (MENU_KEYS.has(event.code)) {
      if (!event.repeat) {
        this.menuToggles += 1;
      }

      // Tab cycles the browser's focus, and while a session is being played
      // there is nothing on the page a focus ring belongs on — the screen this
      // key opens is built and driven from script, and its tabs are pressed
      // with a pointer or with `J`/`M`/`I`. Swallowed outside the repeat guard,
      // like the debug keys, so a held key stays swallowed rather than walking
      // the focus on its second frame.
      event.preventDefault();
    }

    if (HOTBAR_KEYS.has(event.code) && !event.repeat) {
      // **A digit typed into a text field is part of what is being typed.** The
      // listeners in this file are on `document` — which is `BOUND_KEYS`' own
      // stated reason for existing — so the one text input in this game (the
      // name field, `hud/panels/name.ts`) would otherwise send a wield every
      // time somebody named a life *Gudrun the 3rd*. The target is asked rather
      // than the lock, because the field is open before a session and there is
      // no lock to ask about.
      //
      // **No `preventDefault`**, unlike Tab and the debug keys: a digit is a
      // character, nothing in a browser acts on it, and swallowing it would be
      // the one way to break the field this guard is here to protect.
      //
      // `repeat` for the drop verb's reason: an auto-repeating digit would queue
      // thirty wields a second at the same handle, and every one after the first
      // is a command that writes nothing.
      if (!(event.target instanceof HTMLInputElement)) {
        this.hotbarPresses.push(HOTBAR_CODES.indexOf(event.code));
      }
    }

    if (DEBUG_KEYS.has(event.code)) {
      if (!event.repeat) {
        this.debugToggles += 1;
      }

      // F3 is find-again in a browser and a backquote is a character; neither is
      // something a session wants happening underneath it. Outside the repeat
      // guard, so a held key stays swallowed rather than opening the find bar on
      // its second frame.
      event.preventDefault();
    }

    if (FLY_KEYS.has(event.code)) {
      if (!event.repeat) {
        this.flyToggles += 1;
      }

      // F4 opens the address bar's history in some browsers; swallowed outside
      // the repeat guard for the debug key's reason.
      event.preventDefault();
    }

    if (isLookKey(event.code)) {
      // Arrows scroll the page by default; the view owns them here.
      event.preventDefault();
    }

    this.held.add(event.code);
  };

  private readonly handleKeyUp = (event: KeyboardEvent): void => {
    this.held.delete(event.code);
  };

  private readonly handleMouseDown = (event: MouseEvent): void => {
    // Only while locked, which is also what keeps the click that *acquires* the
    // lock from digging: that one arrives before the browser grants it.
    if (this.lockedNow && event.button === DIG_BUTTON) {
      this.digLatched = true;
    }
  };

  private readonly handleMouseMove = (event: MouseEvent): void => {
    if (!this.lockedNow) {
      return;
    }

    // Inside the lock, so this is the view turning rather than a cursor crossing
    // the page on its way to the button that starts the session.
    this.played = true;

    // Positive yaw turns left and positive pitch looks up, so both deltas are
    // negated: the mouse reports screen-space movement, right and down positive.
    this.pendingYaw -= event.movementX * LOOK_SENSITIVITY;
    this.pendingPitch -= event.movementY * LOOK_SENSITIVITY;
  };

  private readonly handleBlur = (): void => {
    this.releaseAll();
  };

  private releaseAll(): void {
    this.held.clear();
    this.jumpLatched = false;
    this.digLatched = false;
    // A swing goes with the dig it shares a button with: a player who alt-tabbed
    // mid-press did not mean to hit whoever they are facing when they come back.
    this.strikeLatched = false;
    // Interact goes with the walk and not with the export: it is a move in the
    // world, and a player who alt-tabbed mid-press did not mean to talk to
    // whoever they happen to be facing when they come back. Camping goes with
    // it — sleeping through a night on the way back from another window is the
    // same kind of surprise. The panel counts survive, like the export,
    // because a journal or a map is a request about the session.
    this.interactLatched = false;
    this.campLatched = false;
    // The three Phase 7 verbs go with the walk for the same reason: a player
    // who alt-tabbed mid-press did not mean to swing a pick at whatever they
    // are pointing at when they come back, or to empty their hands, or to sleep
    // through an afternoon. The panel count survives, like the journal's.
    this.gatherLatched = false;
    this.dropLatched = false;
    this.waitLatched = false;
    // And chunk Fx-5's, on the same reasoning as the wait beside it: a player who
    // alt-tabbed mid-press did not mean to lie down, or to change their mind
    // about coming round.
    this.restLatched = false;
    // And wave 2D-b's three, on the swing's own reasoning: a player who alt-tabbed
    // mid-press did not mean to shoot whoever they come back facing, or to
    // rummage through their own armour.
    this.looseLatched = false;
    this.wearLatched = false;
    this.doffLatched = false;
    // And the hotbar's, on the same reasoning one door further: a slot press is
    // a verb rather than a request about the session, so it goes where the wear
    // and the doff go and not where the panel toggles stay. A player who left
    // the window mid-press did not mean to change what is in their hand on the
    // frame they come back.
    this.hotbarPresses = [];
    // The stick goes with the keys, and for exactly the reason they do: a tab
    // that lost focus mid-stride stops getting the `pointerup` that would have
    // let go, so a walk held in a field here would be a player walking forever
    // in a tab nobody is looking at. The touch layer clears its own pointer ids
    // on the same event (`input/touch.ts`).
    this.stickForward = 0;
    this.stickStrafe = 0;
    this.stickSprint = false;
    this.pendingYaw = 0;
    this.pendingPitch = 0;
  }
}

/**
 * Every key this file binds, by the verb it fires.
 *
 * Exported for exactly one reason, and it is a testing one: the listeners above
 * are on `document`, so `dev/selfcheck.ts` cannot press a key at a throwaway
 * `Controls` without pressing it at the session's own — a check that
 * synthesised an M would open the real player's map on load. What it can do
 * instead is read the binding table, which is what a key card is a rendering of
 * anyway: **M is bound, and nothing else is bound to it.** Assembled from the
 * sets above rather than replacing them, so the handlers stay a set lookup and
 * this cannot drift into being a second source of truth for one of them.
 */
export const BOUND_KEYS: readonly (readonly [string, ReadonlySet<string>])[] = [
  ["forward", FORWARD_KEYS],
  ["back", BACK_KEYS],
  ["left", LEFT_KEYS],
  ["right", RIGHT_KEYS],
  ["jump", JUMP_KEYS],
  ["sprint", SPRINT_KEYS],
  ["dig", DIG_KEYS],
  ["interact", INTERACT_KEYS],
  ["camp", CAMP_KEYS],
  ["gather", GATHER_KEYS],
  ["drop", DROP_KEYS],
  ["loose", LOOSE_KEYS],
  ["wear", WEAR_KEYS],
  ["doff", DOFF_KEYS],
  ["wait", WAIT_KEYS],
  ["rest", REST_KEYS],
  ["inventory", INVENTORY_KEYS],
  ["journal", JOURNAL_KEYS],
  ["map", MAP_KEYS],
  ["trace", TRACE_KEYS],
  ["menu", MENU_KEYS],
  // The ten digits, listed as one verb because that is what they are: ten slots
  // of one strip, and which slot is the index rather than a second binding.
  ["hotbar", HOTBAR_KEYS],
  ["debug", DEBUG_KEYS],
  // The dev flight toggle and the key that sinks one. Listed like every other
  // binding — what this table is for is *"which verb owns this code"*, and a
  // code nobody had listed would be a code the next verb could quietly take
  // (`selfcheck/render.ts` asserts the whole table is a partition).
  ["fly", FLY_KEYS],
  ["fly-down", SINK_KEYS],
  ["look-left", LOOK_LEFT_KEYS],
  ["look-right", LOOK_RIGHT_KEYS],
  ["look-up", LOOK_UP_KEYS],
  ["look-down", LOOK_DOWN_KEYS],
  ["export", EXPORT_KEYS],
];

/** Returns `true` for a key that turns the view. */
function isLookKey(code: string): boolean {
  return (
    LOOK_LEFT_KEYS.has(code) ||
    LOOK_RIGHT_KEYS.has(code) ||
    LOOK_UP_KEYS.has(code) ||
    LOOK_DOWN_KEYS.has(code)
  );
}

/** Renders a pointer-lock rejection as one HUD-sized sentence. */
function lockErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }

  return String(error);
}

/** Returns `true` when any key of the set is held. */
function anyHeld(held: ReadonlySet<string>, keys: ReadonlySet<string>): boolean {
  for (const code of keys) {
    if (held.has(code)) {
      return true;
    }
  }

  return false;
}

/** Returns 1, -1 or 0 for a pair of opposed key sets. Both held reads as neither. */
function axis(
  held: ReadonlySet<string>,
  positive: ReadonlySet<string>,
  negative: ReadonlySet<string>,
): number {
  let value = 0;

  for (const code of held) {
    if (positive.has(code)) {
      value += 1;
    }

    if (negative.has(code)) {
      value -= 1;
    }
  }

  return Math.sign(value);
}
